import { useState } from 'react';
import api from '../services/api';
import {
  LuExternalLink, LuRefreshCw, LuXCircle, LuUsers, LuCalendarCheck, LuCreditCard,
} from 'react-icons/lu';
import PlanSelectionPage from './PlanSelectionPage';
import { fmtDate } from './trainingUtils';
import './plans.css';

/**
 * Meu Plano — administrador da empresa (cadastro público): plano atual,
 * situação do pagamento, acessos simultâneos em uso, fatura em aberto,
 * troca de plano e cancelamento. Dados vêm de GET /subscription/me
 * (carregado no App.js e repassado aqui).
 */

const STATUS = {
  active: { label: 'Ativo', bg: 'var(--success-soft)', color: 'var(--success)' },
  pending_payment: { label: 'Aguardando pagamento', bg: 'var(--warning-soft)', color: 'var(--warning)' },
  overdue: { label: 'Pagamento em atraso', bg: 'var(--danger-soft)', color: 'var(--danger)' },
  canceled: { label: 'Cancelado', bg: 'var(--surface-3)', color: 'var(--text-2)' },
  pending_plan: { label: 'Plano não escolhido', bg: 'var(--surface-3)', color: 'var(--text-2)' },
};

const BILLING_LABEL = { CREDIT_CARD: 'Cartão de crédito', PIX: 'PIX', BOLETO: 'Boleto' };

export default function MyPlanPage({ subscription, onChanged }) {
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!subscription) return <div className="page-card"><div className="training-hint">Carregando…</div></div>;

  if (subscription.legacy) {
    return (
      <div className="page-card">
        <div className="page-card-title">Meu Plano</div>
        <p style={{ marginTop: 12 }}>Sua empresa tem um contrato direto com a DHR Consultoria. Para alterações, fale com a equipe DHR.</p>
      </div>
    );
  }

  if (changing) {
    return (
      <div className="page-card">
        <PlanSelectionPage
          subscription={subscription}
          mode="change"
          onCancel={() => setChanging(false)}
          onDone={(r) => { setChanging(false); setSuccess(r?.message || 'Plano atualizado.'); onChanged(); }}
        />
      </div>
    );
  }

  const sub = subscription.subscription || {};
  const status = STATUS[subscription.status] || STATUS.active;
  const planLabel = subscription.plan_label || '—';
  const paid = subscription.plan_key && subscription.plan_key !== 'free';
  const usagePct = subscription.max_logins ? Math.min(100, Math.round((subscription.active_logins / subscription.max_logins) * 100)) : 0;
  const downgraded = paid && subscription.effective_plan_key === 'free';

  const handlePay = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.get('/subscription/payment-link');
      if (data.invoice_url) window.open(data.invoice_url, '_blank', 'noopener');
      else setError(data.payment_configured ? 'Não há fatura em aberto no momento.' : 'O pagamento online ainda não está disponível. A equipe DHR vai entrar em contato.');
    } catch (e) {
      setError(e.response?.data?.error || 'Não foi possível obter a fatura.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(`Cancelar o plano ${planLabel}? A cobrança mensal é encerrada e sua empresa volta para o plano Gratuito.`)) return;
    setBusy(true); setError('');
    try {
      await api.post('/subscription/cancel');
      setSuccess('Assinatura cancelada. Sua empresa está no plano Gratuito.');
      onChanged();
    } catch (e) {
      setError(e.response?.data?.error || 'Não foi possível cancelar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Meu Plano</div>
          <div className="page-card-subtitle">{subscription.company?.name}</div>
        </div>
        <div className="myplan-actions">
          {subscription.is_company_admin && (
            <button type="button" className="plans-btn plans-btn--primary" onClick={() => setChanging(true)}><LuRefreshCw /> Alterar plano</button>
          )}
        </div>
      </div>

      {error && <div className="alert-custom danger">{error}</div>}
      {success && <div className="alert-custom success">{success}</div>}
      {downgraded && (
        <div className="billing-banner is-danger">
          <div>
            <strong>Recursos do plano {planLabel} suspensos</strong>
            {subscription.status === 'pending_payment' ? 'O pagamento ainda não foi confirmado.' : 'O pagamento está em atraso.'} Enquanto isso, sua empresa usa os recursos do plano Gratuito.
          </div>
        </div>
      )}

      <div className="myplan-grid">
        <div className="myplan-card">
          <div className="myplan-label">Plano</div>
          <div className="myplan-value">
            {planLabel}
            <span className="training-badge" style={{ background: status.bg, color: status.color }}>{status.label}</span>
          </div>
          <div className="myplan-sub">
            {paid && sub.price !== null ? `R$ ${Number(sub.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} por mês` : 'Sem cobrança'}
          </div>
        </div>

        <div className="myplan-card">
          <div className="myplan-label"><LuUsers /> Acessos simultâneos</div>
          <div className="myplan-value">{subscription.active_logins} <span style={{ fontSize: 15, color: 'var(--text-3)' }}>de {subscription.max_logins}</span></div>
          <div className={`myplan-meter ${usagePct >= 100 ? 'is-full' : ''}`}><span style={{ width: `${usagePct}%` }} /></div>
          <div className="myplan-sub">Usuários da empresa conectados agora. Ao atingir o limite, novos logins aguardam alguém sair.</div>
        </div>

        {paid && (
          <div className="myplan-card">
            <div className="myplan-label"><LuCalendarCheck /> Cobrança</div>
            <div className="myplan-value" style={{ fontSize: 18 }}>
              {sub.next_due_date ? `Próximo vencimento ${fmtDate(sub.next_due_date)}` : 'Aguardando a primeira cobrança'}
            </div>
            <div className="myplan-sub"><LuCreditCard /> {BILLING_LABEL[sub.billing_type] || '—'}{sub.last_payment_at ? ` · último pagamento em ${fmtDate(sub.last_payment_at)}` : ''}</div>
          </div>
        )}
      </div>

      {paid && subscription.is_company_admin && (
        <div className="myplan-actions">
          {['pending_payment', 'overdue'].includes(subscription.status) && (
            <button type="button" className="plans-btn plans-btn--primary" onClick={handlePay} disabled={busy}><LuExternalLink /> Pagar fatura em aberto</button>
          )}
          <button type="button" className="plans-btn plans-btn--danger" onClick={handleCancel} disabled={busy}><LuXCircle /> Cancelar assinatura</button>
        </div>
      )}
    </div>
  );
}
